/**
 * @Copyright 2014-2018 DataVoke, Inc. All rights reserved.
 */

'use strict';

import * as tools from '../utils/tools';
import * as types from '../utils/dvtypes';
import { ClientProperties } from "./entitybase";

class ExtensionMetadata {
    /**
     * Extension meta data class.
     * @param {string} type - A simple string that generalizes the "type" of the extension; used for the Type property and "myType" as the Name property.
     * @param {object|null} [defaults = null] (OPTIONAL) Object with defaults in the correct structure for this object type.
     */
    constructor (type, defaults = null) {
        this.Name = (defaults != null && defaults.Name !== null) ? defaults.Name : `my${type}`;
        this.Scope = (defaults != null && defaults.Scope !== null) ? defaults.Scope : `custom${type}`;
        this.Description = (defaults != null && defaults.Description != null) ? defaults.Description : '';
        this.Category = (defaults != null && defaults.Category != null) ? defaults.Category : '';
        this.Author = (defaults != null && defaults.Author != null) ? defaults.Author : app.dv.cache.session.currentUser.User.LoginID;
        this.Source = (defaults != null && defaults.Source != null) ? defaults.Source : '';
        this.Syntax = (defaults != null && defaults.Syntax != null) ? defaults.Syntax : '';
        this.Keywords = (defaults != null && defaults.Keywords != null) ? defaults.Keywords : '';
        this.Version = '';
    }
}

class ExtensionDefinition {
    /**
     * Extension definition class.
     * @param {string} type - A simple string that generalizes the "type" of the extension; used for the Type property and "myType" as the Name property.
     * @param {object|null} [defaults = null] (OPTIONAL) Object with defaults in the correct structure for this object type.
     */
    constructor (type, defaults = null) {
        this.Metadata = new ExtensionMetadata(type, defaults);

        /**
         * Store the version of the application on the definition when it is created.
         * @type {string}
         */
        this.ClientVersion = app.version;

    }
}

class Extension {
    /**
     * Base class for any extension.
     * @param {string} type - A simple string that generalizes the "type" of the extension; used for the Type property and "myType" as the Name property.
     * @param {object|null} [defaults = null] (OPTIONAL) Object with defaults in the correct structure for this object type.
     */
    constructor (type, defaults = null) {
        this.ExtensionID = tools.tempID();
        this.UID = tools.newUID();
        this.Name = (defaults != null && defaults.Name !== null) ? defaults.Name : `my${type}`;
        this.Type = type;
        this.isCustom = true;

        this.Definition = new ExtensionDefinition(type, defaults);

        this._State = types.EntityStates.NewObject;
    }
}

export const extensionEndpointInvokeSample = `// The following objects and methods are available to you:
//  request - The request object; this object is structured as follows:
//      .args - The arguments of the request; this is the information sent by the client.
//  response - The response object; this object must be passed back in the callback function (below).
//      .error - Any error that occurred or you wish to return to the client.
//      .finished - The flag to indicate if the callback is the "last" for this service process. This
//                  allows the service to "updates" as it processes until it is completed. 
//      .result -   The result value or object that you are returning to the client.
//  callback() - The callback function. Once you have processed the request, send the response object back
//               to the client by using this function: "callback(res);"
//
// NOTES:
//
// Make sure you use proper error handling! The service wrapper will attempt to catch and return any issues, but if you take care of them yourself, you will know what is going on.
//
// Make sure any Promises you use properly handle rejections! Again, the service wrapper will attempt to catch and return any issues, but you are the best source for handling them correctly.
//

try {
    // set the response value to TRUE
    response.result = true;
    
    // no error encountered, so it is NULL.
    response.error = null;
    
    // call the callback function, passing it the response object to return to the client.
    callback(response);
}
catch (e) {
    response.error = e;
    callback(response);
}

// To call this new service endpoint, use the following syntax. 
// Note that the endpoint name will be "custom[EndpointName]", so if you named your endpoint "DoStuff", the "endpoints" property would be "customDoStuff".
// The "requestPropertyObject" is an object or value that will be passed to the service, and will be the "req.args" value sent to the service.
// The callback will be called when the service responds, and the "response" object will be that "res.res" object or value sent by the service.
//
// app.dv.services.call(app.dv.services.endpoints.customMyEndpointName, requestPropertyObject, (response, err) = { // handle response object here });   
//
`;

export class Endpoint extends Extension {
    /**
     * Create an endpoint extension.
     * @param {object|null} [defaults = null] (OPTIONAL) Object with defaults in the correct structure for this object type.
     */
    constructor (defaults = null) {
        super('Endpoint', defaults);

        this.clientProperties = new ClientProperties(types.EntityTypes.Endpoint);

        this.Definition.ServiceName = (defaults != null && defaults.svc != null && defaults.svc !== '') ? defaults.svc : 'Custom';
        this.Definition.EndpointName = (defaults != null && defaults.fn != null && defaults.fn !== '') ? defaults.fn : 'myEndpoint';
        this.Definition.IsPublished = (defaults != null && defaults.IsPublished != null) ? defaults.IsPublished : true;
        this.Definition.IsAuthRequired = (defaults != null && defaults.IsAuthRequired != null) ? defaults.IsAuthRequired : true;
        this.Definition.IsDesignerRequired = (defaults != null && defaults.IsDesignerRequired != null) ? defaults.IsDesignerRequired : false;
        this.Definition.IsSecurityAdminRequired = (defaults != null && defaults.IsSecurityAdminRequired != null) ? defaults.IsSecurityAdminRequired : false;

        this.Definition.Invoke = (defaults != null && defaults.Invoke != null && defaults.Invoke !== '') ? defaults.Invoke : extensionEndpointInvokeSample;
    }
}
